<template>
  <div class="directory-table">
    <div class="tms-dashboard__panel-title-box dashboard__title">
      <div class="dashboard">
        {{ $t('directory.label.directory') }}
        <i
          id="dashboard_refresh"
          class="fa fa-refresh dark flat icon_refresh"
          @click="() => loadDirectories()"
        />
      </div>
      <div class="dashboard_button">
        <HKLButton
          color="darkblue"
          action="reset"
          class="clear-sorting"
          @click="() => resetFilter()"
        />
      </div>
    </div>
    <CommonTable
      ref="directoriesTable"
      :tableContentLists="tableContent"
      :renderHeaderList="headers"
      :defaultOrderKey="'companyName'"
      :defaultOrder="true"
      :interactionFunc="interactionFunc"
      :tableId="tableId"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapState } from 'vuex'
import { computed, unref } from 'vue-demi'
import CommonTable from '@/controls/CommonTable'
import HKLButton from '@/controls/Button/HKLButton'
import HKLExternalLinkIcon from '@/controls/Icon/HKLExternalLinkIcon.vue'
import i18n from '@/utils/i18n'
import dateUtil from '@/utils/dateUtil'
import navigation from '@/utils/navigation'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import { directoryRouteNames } from '@/utils/consts'
import { INPUT_TYPE } from '@/modules/contacts/searchFilter.const'
import { DIR_STATUS, DIR_ACTION, DIR_TYPE } from '../directories/consts'
import {
  buildDashboardDirectoriesStatusList,
  useCanReadDashboardDirectoryPermission,
  useCanApproveDirectoryPermission,
  useCanMaintainPermission,
} from './hook'

export default {
  components: {
    CommonTable,
    HKLButton,
  },
  setup () {
    let returnData = {
      tableId: 'tenancy-directory-requisition-table',
    }

    const emptyValue = i18n.t('emptyValue')
    const formatDate = (item) => {
      return item ? dateUtil.formatDate(item, false) : emptyValue
    }
    const hasReadDirectoryPermission = computed(() => aclHelper.getAccess(tmsPermsTypes.directory.read))
    const hasReadDashboardDirectoryPermission = useCanReadDashboardDirectoryPermission()
    const hasApproveDirectoryPermission = useCanApproveDirectoryPermission()
    const hasMaintainPermission = useCanMaintainPermission()

    returnData = {
      ...returnData,
      formatDate,
      hasReadDirectoryPermission,
      hasReadDashboardDirectoryPermission,
      hasApproveDirectoryPermission,
      hasMaintainPermission,
    }

    const headers = computed(() => {
      const list = [
        {
          title: i18n.t('tenancy.companyName'),
          name: 'companyName',
        },
        {
          title: i18n.t('tenancy.tradeName'),
          name: 'tradeName',
        },
        {
          title: i18n.t('tenancy.building'),
          name: 'building',
        },
        {
          title: i18n.t('directory.label.earliestRequestDate'),
          name: 'earliestRequestDate',
          formatRule: formatDate,
        },
        {
          title: i18n.t('directory.label.earliestEffectiveDate'),
          name: 'earliestEffectiveDate',
          formatRule: formatDate,
        },
        {
          title: i18n.t('directory.label.numberOfRequisition'),
          name: 'numberOfRequisition',
        },
      ]
      if (unref(hasMaintainPermission)) {
        list.push({
          title: i18n.t('directory.maintainOrderTitle'),
          name: 'buildingView',
          inputType: INPUT_TYPE.COMPONENT,
          component: HKLExternalLinkIcon,
        })
      }
      return list
    })
    const navToDirectoryList = (item) => {
      navigation.toPage(directoryRouteNames.tenancyDirectories, { id: item.id })
    }
    const navToDirectoryOrder = (item, _) => {
      if (!lodash.get(item, 'allowLinkByColumn.buildingView')) return
      navigation.toPage(
        directoryRouteNames.directoryOrder,
        {},
        item.buildingView,
      )
    }
    const interactionFunc = {
      tradeName: navToDirectoryList,
      buildingView: navToDirectoryOrder,
    }

    returnData = {
      ...returnData,
      headers,
      interactionFunc,
    }

    return returnData
  },
  data () {
    return {
      items: [],
    }
  },
  computed: {
    ...mapGetters('tables', ['getTableInfoById']),
    ...mapGetters('dashboard', ['isDirectoriesListStale']),
    ...mapState('dashboard', { getDirectoriesInfo: 'directories' }),
    tenanciesMap () {
      const tenancyMapFunc = lodash.flow(
        lodash.partialRight(lodash.filter, item => item.tenancy),
        lodash.partialRight(lodash.map, item => item.tenancy),
        lodash.partialRight(lodash.keyBy, item => item.id),
      )
      return this.items.length ? tenancyMapFunc(this.items) : null
    },
    tableContent () {
      if (!this.tenanciesMap || !this.items.length) {
        return []
      }
      const groups = lodash.groupBy(this.items, item => item.tenancyId)
      const dateSortFunc = date => dateUtil.getTimeMillis(date, Number.MAX_SAFE_INTEGER)
      const ret = Object.keys(groups).map(tenancyId => {
        const tenancy = this.tenanciesMap[tenancyId]
        const group = groups[tenancyId] || []
        const groupDirectoryIds = new Set(group.map(item => item.id))
        const filteredGroup = group.filter(item => {
          const childId = lodash.get(item, 'child.id')
          return !childId || !groupDirectoryIds.has(childId)
        })
        const submittedHistories = lodash.flatMap(filteredGroup, item => {
          // Get the last published time of the directory
          const publishedHistories = lodash(item.histories || [])
            .filter(history => history.status === DIR_STATUS.PUBLISHED && history.detail.action === DIR_ACTION.ADD).value()
          const lastPublishedHistory = lodash.maxBy(publishedHistories, history => dateUtil.getTimeMillis(history.createdAt))
          const lastPublishedTime = lastPublishedHistory ? dateUtil.getTimeMillis(lastPublishedHistory.createdAt) : Number.MIN_SAFE_INTEGER
          // Exclude all the histories before `lastPublishedTime`
          return item.histories.filter(history =>
            dateUtil.getTimeMillis(history.createdAt) > lastPublishedTime &&
              history.status === DIR_STATUS.SUBMITTED,
          )
        })
        const earliestRequestDate = lodash.minBy(
          submittedHistories.map(item => item.createdAt), dateSortFunc)
        const earliestEffectiveDate = lodash.minBy(
          groups[tenancyId].map(item => this.getRealEffectiveDate(item)),
          dateSortFunc)
        const types = lodash.uniq(lodash.map(groups[tenancyId], 'type')).sort()
        const building = lodash.get(tenancy, 'building.code')
        let floor
        if (lodash.isEqual(types, [DIR_TYPE.FLOOR])) {
          floor = lodash.flow(
            (items) => items.map((item) => ({
              floor: item.floor,
              engName: item.companies[0].engName,
              submittedAt: lodash.flow(
                (items) => lodash.filter(items, { status: DIR_STATUS.SUBMITTED }),
                (items) => lodash.orderBy(items, ['createdAt'], ['desc']),
                lodash.head,
                (item) => lodash.get(item, 'createdAt'),
              )(item.histories),
            })),
            (items) => lodash.orderBy(items, ['engName', 'submittedAt'], ['asc', 'asc']),
            lodash.head,
            (item) => lodash.get(item, 'floor'),
          )(groups[tenancyId])
        }
        const numberOfRequisition = `${groups[tenancyId].length} (${types.join(', ')})`
        const isEntitledBuilding = filteredGroup.some(item => !!item.isEntitledBuilding)
        return {
          id: tenancyId,
          companyName: lodash.get(tenancy, 'company.name'),
          tradeName: lodash.get(tenancy, 'tradeName') || '--',
          building,
          floor,
          earliestRequestDate,
          earliestEffectiveDate,
          numberOfRequisition,
          types,
          allowLink: this.hasReadDirectoryPermission,
          allowLinkByColumn: {
            buildingView: isEntitledBuilding,
          },
          buildingView: this.hasMaintainPermission ? {
            type: types.length === 2 ? DIR_TYPE.LOBBY : types[0],
            building,
            floor,
          } : undefined,
        }
      })
      return ret
    },
  },
  async mounted () {
    await this.hydrateOrFetchDirectories()
  },
  activated () {
    this.hydrateOrFetchDirectories()
  },
  methods: {
    async loadDirectories () {
      const status = buildDashboardDirectoriesStatusList()
      this.items = await this.$store.dispatch('dashboard/loadDirectoriesForEngineer', { status }) || []
    },
    async hydrateOrFetchDirectories () {
      const dirListInStore = lodash.get(this.getDirectoriesInfo, 'contentList') || []
      if (dirListInStore.length && !this.isDirectoriesListStale) {
        this.items = dirListInStore
      } else {
        await this.loadDirectories()
      }
    },
    resetFilter () {
      this.$refs.directoriesTable.changeKeyAndOrder('companyName', true)
    },
    getRealEffectiveDate (item) {
      if (item.action === DIR_ACTION.ADD) {
        return item.effectiveDate
      } else {
        return item.removeDate
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~styles/dashboard/tenant";

.dashboard__title {
  padding-bottom: 15px
}
.directory-table {
  --width-building: 9%;
  --width-request-date: 11%;
  --width-effective-date: 13%;
  --width-requisition: 13%;
  --width-building-view: 10%;
  --width-company-name: calc((100% - var(--width-building) - var(--width-request-date) - var(--width-effective-date) - var(--width-requisition)) / 2);
  --width-trade-name: var(--width-company-name);

  min-height: 180px;
  & ::v-deep .table-list {
    width: 100% !important;
  }
  & ::v-deep .table-content {
    width: 100% !important;
  }
  & ::v-deep .common-table-header-container {
    min-width: 100% !important;
    max-width: 100% !important;
  }
  & ::v-deep .common-table {
    min-width: 100% !important;
    max-width: 100% !important;
    min-height: unset;
    margin: 0;
    .companyName {
      width: var(--width-company-name);
    }
    .tradeName {
      width: var(--width-trade-name);
    }
    .building {
      width: var(--width-building);
    }
    .earliestRequestDate {
      width: var(--width-request-date);
    }
    .earliestEffectiveDate {
      width: var(--width-effective-date);
    }
    .numberOfRequisition {
      width: var(--width-requisition);
    }
    .buildingView {
      width: var(--width-building-view);
    }
    &:has(.buildingView) {
      --width-company-name: calc((100% - var(--width-building) - var(--width-request-date) - var(--width-effective-date) - var(--width-requisition) - var(--width-building-view)) / 2);
      --width-trade-name: var(--width-company-name);
    }
  }
}
</style>
